import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
    GpsFixed,
    ViewInAr,
    Keyboard,
    Mouse,
    Monitor,
    RocketLaunch,
    Radar,
    School,
    KeyboardArrowDown,
    Add,
    Close,
    Check,
    Home,
    MenuBook, // Wiki
    Book, // Blog
    UploadFile,
    ManageHistory as ManagerIcon,
    Dashboard,
    Refresh,
    Warning,
    Sell,
    Radio,
    KeyboardCommandKey,
    CheckCircle as SyncedIcon,
    Lock,
    PlayArrow,
    CloudDone,
    Sync
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useLanguage } from '@/contexts/LanguageContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useUI } from '@/contexts/UIContext';
import { parseConfigFile } from '@/utils/ConfigParser';
import { BUILTIN_CONFIGS } from '@/constants/builtinConfigs';
import { COMING_SOON_MODULES } from '@/utils/constants';
import { usePro } from '@/contexts/ProContext';
import { storageService } from '@/utils/storageService';
import { getBridge, capabilities } from '@/utils/bridge';
import { track } from '@/utils/analytics';
import SyncStatusIndicator from '@/components/Layouts/SyncStatusIndicator';
import ExportCfgModal from '@/components/Modal/ExportCfgModal';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const scaleIn = keyframes`
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const SidebarTitle = styled.h2`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 1.9rem;
    font-weight: 800;
    text-transform: uppercase;
    color: white;
    margin: 10px 0 12px 14px;
    letter-spacing: 1px;
    display: flex;
    align-items: center;
    
    span {
        color: #7260ff;
        text-shadow: 0 0 10px rgba(114, 96, 255, 0.6);
    }
`;

const SidebarContainer = styled.div`
    width: 100%;
    height: 100%;
    background: rgba(15, 23, 42, 0.4); 
    backdrop-filter: blur(10px);
    border-right: 1px solid rgba(255, 255, 255, 0.05);
    display: flex;
    flex-direction: column;
    padding: 20px 10px;
    gap: 8px;
`;

const NavItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    width: 100%;
    flex-shrink: 0;
    padding: 12px 16px;
    padding-left: 16px;
    background: ${props => props.$active ? 'linear-gradient(90deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0) 100%)' : 'transparent'};
    border: none;
    border-left: 3px solid ${props => props.$active ? '#6366f1' : 'transparent'};
    border-radius: 0 8px 8px 0;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    outline: none;
    position: relative;

    &:hover {
        background: ${props => props.$active ? 'linear-gradient(90deg, rgba(99, 102, 241, 0.25) 0%, rgba(99, 102, 241, 0) 100%)' : 'rgba(255, 255, 255, 0.03)'};
    }

    svg {
        font-size: 20px;
        color: ${props => props.$active ? '#a78bfa' : (props.$checked ? '#d8b4fe' : 'rgba(255, 255, 255, 0.4)')};
        transition: color 0.2s;
    }

    span {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-weight: ${props => props.$active ? '600' : (props.$checked ? '600' : '500')};
        color: ${props => props.$active ? '#fff' : (props.$checked ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)')};
        transition: color 0.2s;
        flex-grow: 1;
        text-align: left;
    }
`;


const CategoryLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 10px 16px 5px;
    margin-top: 10px;
    opacity: ${props => props.$disabled ? 0.3 : 1};
    transition: opacity 0.3s ease;
`;

const SidebarButtonModules = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    opacity: ${props => props.$disabled ? 0.3 : 1};
    pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
    transition: opacity 0.3s ease;
`;

const ActiveIcon = styled(Check)`
    font-size: 16px !important;
    color: #ec4899;
    filter: drop-shadow(0 0 4px rgba(236, 72, 153, 0.6));
    flex-shrink: 0;
`;

const SubMenuContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    transition: max-height 0.3s ease;
`;

const SubNavItem = styled(NavItem)`
    padding-left: 48px;
    background: ${props => props.$active ? 'rgba(99, 102, 241, 0.1)' : 'transparent'};
    border-left: 3px solid ${props => props.$active ? '#6366f1' : 'transparent'};
    
    &:hover {
        background: ${props => props.$active ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255, 255, 255, 0.02)'};
    }

    span {
        font-size: 12px;
        font-weight: ${props => props.$active ? '600' : (props.$checked ? '600' : '400')};
        color: ${props => props.$active ? '#fff' : (props.$checked ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)')};
    }
`;

const ApplyButton = styled.button`
    margin-top: auto;
    width: 100%;
    padding: 14px;
    //background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    background: linear-gradient(135deg, #6365f18f 0%, rgba(245, 132, 255, 0.51)100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
    text-transform: uppercase;

    &:hover {
        background: linear-gradient(135deg, #6365f1ff 0%, rgba(245, 132, 255, 1)100%);
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
        filter: brightness(1.1);
    }

    &:active {
        transform: translateY(0);
    }
`;

const LaunchGameButton = styled(ApplyButton)`
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
        box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
    }

    svg {
        margin-left: 8px;
    }

    /* En el navegador no hay forma de lanzar CS2 (utils/bridge.js: launchCS2
       es un no-op declarado). Se muestra apagado en vez de esconderlo, para
       que se entienda que la app de escritorio existe y hace esto. */
    &:disabled {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: none;
        color: rgba(255, 255, 255, 0.3);
        cursor: not-allowed;
    }
`;

const ExportCfgButton = styled(ApplyButton)`
    background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: linear-gradient(135deg, #818cf8 0%, #a855f7 100%);
        box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
    }
`;

const pendingPulse = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.5); }
    50% { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0); }
`;

const ApplyCfgButton = styled.button`
    width: 100%;
    padding: 12px 14px;
    background: ${props => props.$hasPending
        ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(79, 70, 229, 0.9) 100%)'
        : 'rgba(99, 102, 241, 0.12)'
    };
    border: 1px solid ${props => props.$hasPending
        ? 'rgba(99, 102, 241, 0.8)'
        : 'rgba(99, 102, 241, 0.25)'
    };
    color: ${props => props.$hasPending ? '#fff' : 'rgba(255,255,255,0.5)'};
    border-radius: 8px;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 1px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.25s ease;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
    ${props => props.$hasPending && css`
        animation: ${pendingPulse} 2s ease-in-out infinite;
    `}

    &:hover:not(:disabled) {
        background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
        border-color: rgba(99, 102, 241, 1);
        color: #fff;
        box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4);
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
`;

const PendingDot = styled.span`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #facc15;
    box-shadow: 0 0 6px rgba(250, 204, 21, 0.8);
    flex-shrink: 0;
`;

const AutoloadDialogOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.72);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
`;

const AutoloadDialogBox = styled.div`
    background: rgba(12, 12, 22, 0.98);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 14px;
    padding: 22px 20px 18px;
    width: 300px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(99, 102, 241, 0.08);
`;

const DialogTitle = styled.p`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: rgba(255,255,255,0.45);
    margin: 0;
`;

const DialogChoices = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DialogChoice = styled.button`
    width: 100%;
    padding: 11px 14px;
    background: ${props => props.$primary
        ? 'linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(79,70,229,0.18) 100%)'
        : 'rgba(255,255,255,0.03)'};
    border: 1px solid ${props => props.$primary
        ? 'rgba(99,102,241,0.45)'
        : 'rgba(255,255,255,0.08)'};
    border-radius: 9px;
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 3px;
    transition: all 0.18s ease;

    &:hover {
        background: ${props => props.$primary
            ? 'linear-gradient(135deg, rgba(99,102,241,0.32) 0%, rgba(79,70,229,0.32) 100%)'
            : 'rgba(255,255,255,0.07)'};
        border-color: ${props => props.$primary
            ? 'rgba(99,102,241,0.7)'
            : 'rgba(255,255,255,0.18)'};
    }
`;

const DialogChoiceLabel = styled.span`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${props => props.$primary ? '#a5b4fc' : 'rgba(255,255,255,0.45)'};
`;

const DialogChoiceName = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${props => props.$primary ? '#fff' : 'rgba(255,255,255,0.7)'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DialogCancelBtn = styled.button`
    background: none;
    border: none;
    color: rgba(255,255,255,0.25);
    font-size: 12px;
    cursor: pointer;
    text-align: center;
    padding: 2px;
    transition: color 0.15s;

    &:hover { color: rgba(255,255,255,0.5); }
`;

// --- Profile Selector Components ---

const ProfileSection = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 0px;
    padding: 0 6px;
    padding-left: 8px;
    box-sizing: border-box;
`;

const ProfileButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    background: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.03)' : 'linear-gradient(90deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)'};
    border: 1px solid ${props => props.$disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(99, 102, 241, 0.3)'};
    border-radius: 12px;
    color: white;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 13px;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${props => props.$disabled ? 0.5 : 1};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'};

    &:hover {
        background: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.03)' : 'linear-gradient(90deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.15) 100%)'};
        border-color: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(167, 139, 250, 0.6)'};
        transform: ${props => props.$disabled ? 'none' : 'translateY(-1px)'};
        box-shadow: ${props => props.$disabled ? 'none' : '0 0 15px rgba(99, 102, 241, 0.25)'};
    }

    div {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        padding-right: 20px;
    }

    span {
        font-weight: 700;
        font-size: 9px;
        color: #a78bfa;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    strong {
        text-align: left;
        width: 100%;
        font-weight: 700;
        color: #fff;
        font-size: 15px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
    }

    svg {
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #a78bfa;
        filter: drop-shadow(0 0 2px rgba(167, 139, 250, 0.5));
    }
`;

const DropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    left: 6px;
    width: 100%;
    margin-top: 8px;
    background: #1e1b2e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    z-index: 100;
    animation: ${fadeIn} 0.2s ease;
`;

const DropdownItem = styled.div`
width: 100%;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);

    &:hover {
        background: rgba(99, 102, 241, 0.1);
        color: #fff;
    }

    &:last-child {
        border-bottom: none;
    }

    svg {
        font-size: 16px;
        color: #6366f1;
    }
`;

const ConfigList = styled.div`
    max-height: 250px;
    overflow-y: auto;
    background: rgba(0,0,0,0.2);
    width: 100%;
    flex-direction: column;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.1);
        border-radius: 2px;
    }
`;

const ConfigItem = styled(DropdownItem)`
width: 100%;
    padding-left: 16px;
    font-size: 12px;
    color: ${props => props.$active ? '#a78bfa' : 'rgba(255,255,255,0.6)'};
    background: ${props => props.$active ? 'rgba(99, 102, 241, 0.05)' : 'transparent'};

    &:hover {
        background: rgba(255, 255, 255, 0.05);
    }
`;

// --- Status Block Components ---

const StatusBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    margin: 0 6px 16px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
`;

const StatusItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 10px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.8px;
`;

const StatusDot = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.$active ? '#4ade80' : 'rgba(255, 255, 255, 0.1)'};
    box-shadow: ${props => props.$active ? '0 0 8px #4ade80' : 'none'};
    transition: all 0.3s ease;
    
    ${props => props.$loading && css`
        background: #6366f1;
        box-shadow: 0 0 8px #6366f1;
        animation: ${spin} 1s linear infinite;
    `}
`;

const StatusText = styled.span`
    color: ${props => props.$active ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
    transition: all 0.3s ease;
`;

const SyncAnimated = styled(Sync)`
    font-size: 12px !important;
    color: #6366f1;
    margin-left: auto;
    animation: ${spin} 2s linear infinite;
`;

const OverlayFadeIn = keyframes`
    from { opacity: 0; backdrop-filter: blur(0px); }
    to { opacity: 1; backdrop-filter: blur(8px); }
`;

const PulseRing = keyframes`
    0% { transform: scale(0.8); opacity: 0.5; border-color: rgba(16, 185, 129, 0.8); }
    50% { transform: scale(1.2); opacity: 0.2; border-color: rgba(16, 185, 129, 0.4); }
    100% { transform: scale(0.8); opacity: 0.5; border-color: rgba(16, 185, 129, 0.8); }
`;

const LogoSpin = keyframes`
    0% { transform: rotate(0deg) scale(1); filter: drop-shadow(0 0 10px rgba(16, 185, 129, 0.5)); }
    50% { transform: rotate(180deg) scale(1.1); filter: drop-shadow(0 0 25px rgba(16, 185, 129, 0.8)); }
    100% { transform: rotate(360deg) scale(1); filter: drop-shadow(0 0 10px rgba(16, 185, 129, 0.5)); }
`;

const LaunchOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(5, 20, 10, 0.85);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 0 0 200px rgba(16, 185, 129, 0.15);
    animation: ${OverlayFadeIn} 0.3s ease-out forwards;
    pointer-events: all;
`;

const OverlayContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
`;

const LaunchSpinner = styled.div`
    width: 80px;
    height: 80px;
    border: 4px solid transparent;
    border-top-color: #10b981;
    border-bottom-color: #059669;
    border-radius: 50%;
    animation: ${LogoSpin} 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: -10px;
        left: -10px;
        right: -10px;
        bottom: -10px;
        border-radius: 50%;
        border: 2px solid rgba(16, 185, 129, 0.3);
        animation: ${PulseRing} 2s ease-in-out infinite;
    }
`;

const LaunchText = styled.h2`
    color: #fff;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 24px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 4px;
    text-shadow: 0 0 20px rgba(16, 185, 129, 0.6);
    margin: 0;
`;

const LaunchSubtext = styled.p`
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    margin: 0;
    font-weight: 500;
`;


const SidebarCheckbox = styled.div`
    width: 15px;
    height: 15px;
    border-radius: 4px;
    border: 1.5px solid ${({ $checked, $restricted }) =>
        $restricted ? 'rgba(251, 191, 36, 0.3)' :
        $checked ? '#ec4899' : 'rgba(255, 255, 255, 0.2)'};
    background: ${({ $checked, $restricted }) =>
        $restricted ? 'transparent' :
        $checked ? 'rgba(236, 72, 153, 0.2)' : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s ease;
    margin-left: auto;
    cursor: pointer;
    box-shadow: ${({ $checked }) => $checked ? '0 0 6px rgba(236, 72, 153, 0.3)' : 'none'};

    &:hover {
        border-color: ${({ $restricted }) => $restricted ? 'rgba(251, 191, 36, 0.5)' : '#ec4899'};
        background: ${({ $restricted }) => $restricted ? 'transparent' : 'rgba(236, 72, 153, 0.1)'};
    }
`;

const DashboardSidebar = ({ activeTab, setActiveTab, onPrefetch }) => {
    const { t } = useLanguage();
    const {
        configName,
        userConfigs,
        resetConfig,
        loadUserConfig,
        getTemplateData,
        fetchUserConfigs,
        activeModules,
        updateConfigGlobal,
        toggleModule,
        activateModules,
        setAutoLoad,
        isLoadingConfigs,
        syncStatus,
        lastSyncTime,
        syncConfigsToGame,
        isSaving: globalIsSaving,
        hasUnsyncedChanges,
        manualSaveAndSync,
        cs2Path
    } = useConfig();
    const { hasFeature } = usePro();
    // Un solo permiso para los 7 módulos: se destraban juntos.
    const puedeAvanzados = hasFeature('modulos_avanzados');
    const { showPrompt, showConfirm, showCreateConfig } = useUI();
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);

    // Filter out builtin configs for the dropdown list
    const [expandedGroups, setExpandedGroups] = useState({});

    // Filter out builtin configs for the dropdown list
    const sortedConfigs = React.useMemo(() => {
        if (!userConfigs) return [];
        return userConfigs
            .filter(c => !c.isBuiltin)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [userConfigs]);


    // Auto-expand group if active tab is inside it
    useEffect(() => {
        const parent = items.find(item => item.children && item.children.some(child => child.id === activeTab));
        if (parent && !expandedGroups[parent.id]) {
            setExpandedGroups(prev => ({ ...prev, [parent.id]: true }));
        }
    }, [activeTab]);

    const toggleGroup = (id) => {
        setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleSwitch = (name) => {
        const configToLoad = userConfigs.find(c => c.name === name);
        if (configToLoad) {
            loadUserConfig(configToLoad);
            if (!configToLoad.isBuiltin) {
                setAutoLoad(configToLoad.name).catch(console.error);
            }
        }
        setIsOpen(false);
    };

    const handleStartCreate = async () => {


        setIsOpen(false);
        // Suggested name using translation
        let baseName = t('NEW_CONFIG_DEFAULT_NAME');
        let nextNum = 1;
        const existingNames = userConfigs ? userConfigs.map(c => c.name.toLowerCase()) : [];

        let candidate = baseName;
        while (existingNames.includes(candidate.toLowerCase())) {
            nextNum++;
            candidate = `${baseName} ${nextNum}`;
        }

        const result = await showCreateConfig({
            title: t('CREATE_NEW_CONFIG'),
            confirmText: t('CREATE_ACTION'),
            defaultValue: candidate,
            userConfigs: userConfigs
        });

        if (result && result.name) {
            await handleSaveCreation(result.name, result.template);
        }
    };

    const handleSaveCreation = async (name, preset) => {
        if (!name.trim()) return;

        if (userConfigs.some(c => c.name.toLowerCase() === name.trim().toLowerCase())) {
            alert("Config name already exists!");
            return;
        }

        setIsSaving(true);
        try {
            let templateData;
            if (['empty', 'default', 'recommended'].includes(preset) || preset.startsWith('builtin-')) {
                if (preset.startsWith('builtin-')) track('preset_use', { kind: 'pro-config', id: preset });
                templateData = getTemplateData(preset);
            } else {
                const templateCfg = userConfigs.find(c => c.id === preset);
                if (templateCfg) {
                    templateData = { ...templateCfg.settings };
                } else {
                    templateData = getTemplateData('default');
                }
            }

            // Construct the config object
            const configBody = {
                name: name.trim(),
                settings: templateData,
                // For Electron/Local, we can generate an ID or let the backend handle it.
                // saveLocalConfig handles ID generation if missing.
            };

            let success = false;
            let finalId = null;

            // Use StorageService for unified saving
            const result = await storageService.saveConfig(configBody);
            if (result.success) {
                success = true;
                finalId = result.config?.id || result.id;
            } else {
                console.error(result.message);
            }

            if (success) {
                const freshConfigs = await fetchUserConfigs(true);
                // Load the newly created config immediately
                const newConfig = freshConfigs?.find(c => c.name === name.trim());
                if (newConfig) {
                    loadUserConfig(newConfig);
                }

                // Automatically set as AutoLoad (User Request)
                try {
                    await setAutoLoad(name.trim());
                } catch (e) {
                    console.error('Failed to auto-set autoload:', e);
                }
                setTimeout(() => syncConfigsToGame(), 500);
            } else {
                alert('Failed to create config');
            }
        } catch (error) {
            console.error(error);
            alert('Error creating config');
        } finally {
            setIsSaving(false);
        }
    };





    const items = [
        { id: 'crosshair', label: 'Crosshair', icon: <GpsFixed /> },
        { id: 'mouse', label: 'Mouse / Sens', icon: <Mouse /> },
        { id: 'viewmodel', label: 'Viewmodel', icon: <ViewInAr /> },
        { id: 'hud', label: 'HUD / Radar', icon: <Radar /> },
        { id: 'binds-basic', label: 'Basic Binds', icon: <Keyboard />, moduleKey: 'binds_basic' },
        { id: 'binds-special', label: 'Special Binds', icon: <KeyboardCommandKey />, moduleKey: 'binds_special' },
        { id: 'binds-custom', label: t('BINDS_CUSTOM_TITLE'), icon: <Sell />, moduleKey: 'binds_custom' },
        { id: 'radio', label: t('RADIO_COMMUNICATION_TITLE'), icon: <Radio />, moduleKey: 'radio' },
        { id: 'practice', label: 'Practice', icon: <School />, moduleKey: 'practice' },
        { id: 'video', label: 'Video / FPS', icon: <Monitor />, moduleKey: 'graphics' },
        { id: 'launch', label: 'Launch Options', icon: <RocketLaunch />, moduleKey: 'launch_options' },
    ];

    // Filter out builtin configs for the dropdown list


    const isModuleActive = (id, moduleKey) => {
        if (!activeModules) return false;
        if (id === 'launch') return false; // Managed separately
        // Direct match
        if (activeModules.includes(id)) return true;
        // Group match (check if moduleKey provided)
        if (moduleKey && activeModules.includes(moduleKey)) return true;
        // Check "binds" specifically for binds children
        if (id.startsWith('binds') && activeModules.includes('binds')) return true;

        return false;
    };



    const [timeAgo, setTimeAgo] = useState('');
    // Removed local timeAgo logic


    const [isLaunching, setIsLaunching] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);

    // Shell nativo vs navegador. Se resuelve en un efecto y NO leyendo
    // capabilities.launch durante el render: en el server siempre da false (no
    // existe window), así que leerlo en el render haría que el HTML del
    // servidor y el primer render del cliente difieran dentro del shell —
    // hydration mismatch. Arrancando en false coinciden, y el efecto corrige.
    const [canLaunchGame, setCanLaunchGame] = useState(false);
    useEffect(() => {
        setCanLaunchGame(capabilities.launch);
    }, []);
    const [autoloadDialog, setAutoloadDialog] = useState(null); // { currentAutoload: string }

    const handleApplyToGame = async () => {
        if (!configName || globalIsSaving) return;

        const autoexecConfig = userConfigs?.find(c => c.name === 'autoexec');
        const currentAutoload = autoexecConfig?.settings?.autoloadConfig;

        if (currentAutoload && currentAutoload !== configName) {
            setAutoloadDialog({ currentAutoload });
            return;
        }

        await manualSaveAndSync();
    };

    const handleDialogActivateCurrent = async () => {
        setAutoloadDialog(null);
        await setAutoLoad(configName);
        await manualSaveAndSync();
    };

    const handleDialogKeepAutoload = async () => {
        setAutoloadDialog(null);
        await manualSaveAndSync();
    };

    const handleLaunchGame = async () => {
        if (!configName) return;

        setIsLaunching(true);

        const activeC = userConfigs?.find(c => c.name === configName);
        if (!activeC) {
            setIsLaunching(false);
            return;
        }

        const safeName = configName.replace(/[^a-zA-Z0-9_-]/g, '_');
        const execArg = `+exec configstrike_${safeName}.cfg`;

        let launchArgs = [execArg];

        // Launch Options from settings:
        const userOpts = activeC.settings?.launch_options;

        if (userOpts) {
            if (typeof userOpts === 'object') {
                // Handle Object from LaunchOptionsPage
                if (userOpts.novid) launchArgs.push('-novid');
                if (userOpts.high) launchArgs.push('-high');
                if (userOpts.console) launchArgs.push('-console');
                if (userOpts.fullscreen) launchArgs.push('-fullscreen');

                if (userOpts.width && userOpts.height) {
                    launchArgs.push('-w', userOpts.width.toString());
                    launchArgs.push('-h', userOpts.height.toString());
                }

                if (userOpts.refresh) {
                    launchArgs.push('-refresh', userOpts.refresh.toString());
                }

                if (userOpts.custom_args) {
                    const customSplits = userOpts.custom_args.match(/(?:[^\s"]+|"[^"]*")+/g);
                    if (customSplits) {
                        launchArgs.push(...customSplits);
                    }
                }

                if (userOpts.exec_autoexec) {
                    launchArgs.push('+exec', 'autoexec.cfg');
                }

            } else if (typeof userOpts === 'string') {
                // Legacy String Support
                const splitOpts = userOpts.match(/(?:[^\s"]+|"[^"]*")+/g);
                if (splitOpts) {
                    launchArgs.push(...splitOpts);
                }
            }
        }

        const bridge = getBridge();
        if (bridge?.launchCS2) {
            track('cs2_launch');
            await bridge.launchCS2(launchArgs);
        }

        // Keep overlay for a few seconds for effect
        setTimeout(() => {
            setIsLaunching(false);
        }, 15000);
    };


    return (
        <SidebarContainer>
            {isLaunching && (
                <LaunchOverlay>
                    <OverlayContent>
                        <LaunchSpinner />
                        <LaunchText>{t('OPENING_CS2')}</LaunchText>
                        <LaunchSubtext>GL & HF</LaunchSubtext>
                    </OverlayContent>
                </LaunchOverlay>
            )}

            {autoloadDialog && (
                <AutoloadDialogOverlay>
                    <AutoloadDialogBox>
                        <DialogTitle>¿Qué config activar en CS2?</DialogTitle>
                        <DialogChoices>
                            <DialogChoice $primary onClick={handleDialogActivateCurrent}>
                                <DialogChoiceLabel $primary>La que acabo de editar</DialogChoiceLabel>
                                <DialogChoiceName $primary>{configName}</DialogChoiceName>
                            </DialogChoice>
                            <DialogChoice onClick={handleDialogKeepAutoload}>
                                <DialogChoiceLabel>Mantener la activa</DialogChoiceLabel>
                                <DialogChoiceName>{autoloadDialog.currentAutoload}</DialogChoiceName>
                            </DialogChoice>
                        </DialogChoices>
                        <DialogCancelBtn onClick={() => setAutoloadDialog(null)}>Cancelar</DialogCancelBtn>
                    </AutoloadDialogBox>
                </AutoloadDialogOverlay>
            )}
            {/* Dashboard, Manager, Wiki Nav removed - now in MainSideBar */}

            {/* Status Block */}
            {/* Status Block 
            <StatusBlock>
                <StatusItem>
                    <StatusDot $active={!globalIsSaving} $loading={globalIsSaving} />
                    <StatusText $active={true}>
                        {globalIsSaving ? t('STATUS_SAVING') : t('STATUS_SAVED')}
                    </StatusText>
                    {!globalIsSaving && <CloudDone style={{ fontSize: 12, color: '#4ade80', marginLeft: 'auto' }} />}
                </StatusItem>

                <StatusItem>
                    <StatusDot $active={syncStatus === 'synced'} $loading={syncStatus === 'syncing'} />
                    <StatusText $active={syncStatus === 'synced'}>
                        {syncStatus === 'syncing' ? t('STATUS_SYNCING') : t('STATUS_SYNCED')}
                    </StatusText>
                    {syncStatus === 'synced' && <SyncedIcon style={{ fontSize: 12, color: '#4ade80', marginLeft: 'auto' }} />}
                    {syncStatus === 'syncing' && <SyncAnimated />}
                    </StatusItem>
                )}
            </StatusBlock> */}

            {/* Sidebar Title */}
            <SidebarTitle><span>{t('EDITOR_TITLE_PREFIX')}</span>{t('EDITOR_TITLE_SUFFIX')}</SidebarTitle>

            <SidebarButtonModules $disabled={!configName}>

                {items.map(item => {
                    const isDisabled = !configName;
                    const isRestricted = !puedeAvanzados && COMING_SOON_MODULES.includes(item.moduleKey || item.id);

                    if (item.children) {
                        const isExpanded = expandedGroups[item.id];
                        const isActiveGroup = activeTab === item.id || item.children.some(c => c.id === activeTab);
                        const showActiveIcon = isModuleActive(item.id, item.moduleKey) && !isRestricted;
                        const hasRestrictedChild = !puedeAvanzados && item.children.some(c => COMING_SOON_MODULES.includes(c.moduleKey || item.moduleKey));

                        return (
                            <React.Fragment key={item.id}>
                                <NavItem
                                    $active={isActiveGroup}
                                    $checked={showActiveIcon}
                                    onClick={() => !isDisabled && toggleGroup(item.id)}
                                    style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                >
                                    {item.icon}
                                    <span>{item.label}</span>
                                    {isRestricted && <Lock style={{ fontSize: 14, color: '#fbbf24', opacity: 0.8, marginLeft: '8px' }} />}
                                    {showActiveIcon && <ActiveIcon />}
                                    <KeyboardArrowDown style={{
                                        marginLeft: 'auto',
                                        fontSize: 16,
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                        opacity: isDisabled ? 0.3 : 1
                                    }} />
                                </NavItem>
                                <SubMenuContainer style={{ maxHeight: isExpanded && !isDisabled ? '500px' : '0' }}>
                                    {item.children.map(child => {
                                        const isChildActive = activeTab === child.id;
                                        const isChildRestricted = !puedeAvanzados && COMING_SOON_MODULES.includes(child.moduleKey || item.moduleKey);
                                        return (
                                            <SubNavItem
                                                key={child.id}
                                                $active={isChildActive}
                                                $checked={!isChildRestricted && isModuleActive(child.id, child.moduleKey || item.moduleKey)}
                                                onClick={() => !isDisabled && setActiveTab(child.id)}
                                                onMouseEnter={() => !isDisabled && onPrefetch && onPrefetch(child.id)}
                                                disabled={isDisabled}
                                            >
                                                <span>{child.label}</span>
                                                {isChildRestricted && <Lock style={{ fontSize: 12, color: '#fbbf24', opacity: 0.6, marginLeft: '6px' }} />}
                                                {isModuleActive(child.id, child.moduleKey || item.moduleKey) && !isChildRestricted && <ActiveIcon />}
                                            </SubNavItem>
                                        );
                                    })}
                                </SubMenuContainer>
                            </React.Fragment>
                        );
                    }

                    const isActive = activeTab === item.id;
                    const moduleKey = item.moduleKey || item.id;
                    const showActiveIcon = isModuleActive(item.id, item.moduleKey) && !isRestricted;
                    const showCheckbox = item.id !== 'launch';

                    return (
                        <NavItem
                            key={item.id}
                            $active={isActive}
                            $checked={showActiveIcon}
                            onClick={() => !isDisabled && setActiveTab(item.id)}
                            onMouseEnter={() => !isDisabled && onPrefetch && onPrefetch(item.id)}
                            disabled={isDisabled}
                            style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                            {isRestricted && (
                                <Tooltip title={t('COMING_SOON_LABEL') || 'PRO'} placement="right">
                                    <Lock style={{ fontSize: 13, color: '#fbbf24', opacity: 0.8, marginLeft: 'auto', marginRight: '2px' }} />
                                </Tooltip>
                            )}
                            {showCheckbox && !isRestricted && (
                                <SidebarCheckbox
                                    $checked={showActiveIcon}
                                    $restricted={false}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isDisabled) toggleModule(moduleKey);
                                    }}
                                    title={showActiveIcon ? t('MODULE_ACTIVE') : t('ACTIVATE_MODULE')}
                                >
                                    {showActiveIcon && <Check style={{ fontSize: 10, color: '#ec4899' }} />}
                                </SidebarCheckbox>
                            )}
                            {showCheckbox && isRestricted && (
                                <SidebarCheckbox $checked={false} $restricted={true} style={{ cursor: 'not-allowed' }} />
                            )}
                        </NavItem>
                    );
                })}
            </SidebarButtonModules>

            <div style={{ width: '100%', padding: '0 10px', marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sortedConfigs.length > 1 && (
                    <ProfileSection ref={wrapperRef} style={{ padding: 0, margin: 0 }}>
                        <ProfileButton
                            onClick={() => { if (!isLoadingConfigs) setIsOpen(!isOpen); }}
                            $disabled={isLoadingConfigs}
                            style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: '6px 12px' }}
                        >
                            <div style={{ paddingRight: 20 }}>
                                <span>{t('SELECTED_CONFIG')}</span>
                                <strong>
                                    {isLoadingConfigs ? t('LOADING') :
                                        (configName && configName.trim() !== '') ?
                                            configName
                                            : (userConfigs && userConfigs.some(c => !c.isBuiltin) ? t('SELECT_CONFIG') : t('CREATE_CONFIG'))}
                                </strong>
                            </div>
                            {!isLoadingConfigs && <KeyboardArrowDown style={{ fontSize: 16, position: 'absolute', right: 14 }} />}
                        </ProfileButton>
                        {isOpen && (
                            <DropdownMenu style={{ top: 'auto', bottom: '100%', marginTop: 0, marginBottom: 8 }}>
                                <div style={{ padding: '8px 16px', fontSize: '10px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', fontWeight: 'bold' }}>
                                    {t('MY_CONFIGS_UPPER')}
                                </div>
                                <ConfigList>
                                    {sortedConfigs.map(cfg => (
                                        <ConfigItem
                                            key={cfg.id || cfg.name}
                                            $active={cfg.name === configName}
                                            onClick={() => handleSwitch(cfg.name)}
                                        >
                                            {cfg.name}
                                        </ConfigItem>
                                    ))}
                                </ConfigList>
                            </DropdownMenu>
                        )}
                    </ProfileSection>
                )}
                <ApplyCfgButton
                    onClick={handleApplyToGame}
                    $hasPending={hasUnsyncedChanges}
                    disabled={!configName || globalIsSaving || !cs2Path}
                    title={!cs2Path ? 'CS2 folder not detected' : hasUnsyncedChanges ? 'Hay cambios sin aplicar al juego' : 'Config aplicado al juego'}
                >
                    {globalIsSaving
                        ? <><SyncAnimated /><span>GUARDANDO...</span></>
                        : hasUnsyncedChanges
                            ? <><PendingDot /><span>{t('APPLY_CS2')}</span></>
                            : <><CloudDone style={{ fontSize: 16 }} /><span>{t('APPLY_CS2')}</span></>
                    }
                </ApplyCfgButton>
                {canLaunchGame ? (
                    <LaunchGameButton onClick={handleLaunchGame} style={{ flexDirection: 'column', gap: '1px' }}>
                        <span>{t('LAUNCH_CS2')}</span>
                        <span style={{ fontSize: '0.6rem', opacity: 0.6, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{t('LAUNCH_CS2_SUBTITLE')}</span>
                    </LaunchGameButton>
                ) : (
                    <>
                        {/* El <span> no es decorativo: un <button disabled> no emite
                            eventos de mouse, así que el Tooltip de MUI colgado
                            directamente del botón nunca se mostraría. */}
                        <Tooltip title={t('LAUNCH_CS2_WEB_ONLY')} placement="top">
                            <span style={{ display: 'block', width: '100%' }}>
                                <LaunchGameButton disabled style={{ flexDirection: 'column', gap: '1px', width: '100%' }}>
                                    <span>{t('LAUNCH_CS2')}</span>
                                    <span style={{ fontSize: '0.6rem', opacity: 0.6, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{t('LAUNCH_CS2_SUBTITLE')}</span>
                                </LaunchGameButton>
                            </span>
                        </Tooltip>
                        <ExportCfgButton onClick={() => setExportOpen(true)} style={{ flexDirection: 'column', gap: '1px' }}>
                            <span>{t('EXPORT_CFG')}</span>
                            <span style={{ fontSize: '0.6rem', opacity: 0.6, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{t('EXPORT_CFG_SUBTITLE')}</span>
                        </ExportCfgButton>
                    </>
                )}
            </div>

            <ExportCfgModal isOpen={exportOpen} onClose={() => setExportOpen(false)} />

        </SidebarContainer >
    );
};

export default DashboardSidebar;
